const DEFAULT_ERROR_CODE = "500";

export type ErrorDetails = Record<string, string | undefined>;

export type NormalizedErrorInfo = {
	errorCode: string;
	title: string;
	description: string;
	rawMessage: string;
	details: ErrorDetails;
	stack?: string;
	raw?: string;
};

type RouteErrorResponseLike = {
	status: number;
	statusText?: string;
	data?: unknown;
};

type ExtractErrorInfoOptions = {
	route?: string;
	currentUrl?: string;
	timestamp?: string;
	defaultErrorCode?: string;
};

type KmcmsRequestErrorOptions = {
	statusCode?: number;
	statusText?: string;
	endpoint?: string;
	rawBody?: string;
	method?: string;
};

export class KmcmsRequestError extends Error {
	statusCode?: number;
	statusText?: string;
	endpoint?: string;
	rawBody?: string;
	method?: string;

	constructor(message: string, options: KmcmsRequestErrorOptions = {}) {
		super(message);
		this.name = "KmcmsRequestError";
		this.statusCode = options.statusCode;
		this.statusText = options.statusText;
		this.endpoint = options.endpoint;
		this.rawBody = options.rawBody;
		this.method = options.method;
	}
}

function isObject(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null;
}

function isRouteErrorResponseLike(
	error: unknown
): error is RouteErrorResponseLike {
	return (
		isObject(error) &&
		typeof error.status === "number" &&
		(error.statusText === undefined || typeof error.statusText === "string")
	);
}

function safeStringify(value: unknown) {
	if (typeof value === "string") {
		return value;
	}

	try {
		return JSON.stringify(value, null, 2);
	} catch {
		return String(value);
	}
}

function getErrorTitle(errorCode: string) {
	switch (errorCode) {
		case "400":
			return "Bad Request";
		case "401":
			return "Sign In Required";
		case "403":
			return "Access Denied";
		case "404":
			return "Not Found";
		case "408":
			return "Request Timed Out";
		case "409":
			return "Conflict Detected";
		case "422":
			return "Invalid Data";
		case "429":
			return "Too Many Requests";
		case "500":
			return "Server Error";
		case "502":
			return "Upstream Service Error";
		case "503":
			return "Service Unavailable";
		case "504":
			return "Gateway Timeout";
		default:
			return "Unexpected Error";
	}
}

function getUserFriendlyDescription(errorCode: string, rawMessage: string) {
	switch (errorCode) {
		case "400":
		case "422":
			return "The app sent data the server could not process. Please double-check the request and try again.";
		case "401":
		case "403":
			return "Your account does not have permission to complete this action. Try signing in again or checking your access.";
		case "404":
			return "The page or resource could not be found. It may have been moved, deleted, or the request path may be wrong.";
		case "408":
		case "504":
			return "The request took too long to complete. This usually means the upstream service was slow or did not respond in time.";
		case "429":
			return "The app or upstream API is being rate limited right now. Wait a moment and try again.";
		case "500":
		case "502":
		case "503":
			return "The server hit a problem while loading this page. Please try again, and if it keeps happening send this screen to the developer.";
		default:
			if (rawMessage) {
				return "The app hit an unexpected error. Please try again, and if it keeps happening send this screen to the developer.";
			}
			return "Something unexpected went wrong. Please try again.";
	}
}

function extractRawMessage(data: unknown, fallback = "Unknown error") {
	if (typeof data === "string" && data.trim()) {
		return data;
	}

	if (isObject(data)) {
		const candidateKeys = [
			"message",
			"error",
			"errorMessage",
			"body",
			"detail"
		] as const;
		for (const key of candidateKeys) {
			const value = data[key];
			if (typeof value === "string" && value.trim()) {
				return value;
			}
		}
	}

	return fallback;
}

function buildCommonDetails(options: ExtractErrorInfoOptions) {
	const timestamp = options.timestamp ?? new Date().toISOString();
	return {
		route: options.route,
		currentUrl: options.currentUrl,
		timestamp
	};
}

export function extractErrorInfo(
	error: unknown,
	options: ExtractErrorInfoOptions = {}
): NormalizedErrorInfo {
	const commonDetails = buildCommonDetails(options);

	if (isRouteErrorResponseLike(error)) {
		const errorCode = String(error.status);
		const rawMessage = extractRawMessage(
			error.data,
			error.statusText || "Route error response"
		);
		const raw = error.data === undefined ? undefined : safeStringify(error.data);

		return {
			errorCode,
			title: getErrorTitle(errorCode),
			description: getUserFriendlyDescription(errorCode, rawMessage),
			rawMessage,
			raw,
			details: {
				...commonDetails,
				errorType: "RouteErrorResponse",
				statusText: error.statusText,
				responseData: raw
			}
		};
	}

	if (error instanceof KmcmsRequestError) {
		const errorCode = String(error.statusCode ?? options.defaultErrorCode ?? DEFAULT_ERROR_CODE);

		return {
			errorCode,
			title: getErrorTitle(errorCode),
			description: getUserFriendlyDescription(errorCode, error.message),
			rawMessage: error.message,
			raw: error.rawBody,
			stack: error.stack,
			details: {
				...commonDetails,
				errorType: error.name,
				statusText: error.statusText,
				endpoint: error.endpoint,
				method: error.method,
				rawBody: error.rawBody
			}
		};
	}

	if (error instanceof Error) {
		const errorCode = String(options.defaultErrorCode ?? DEFAULT_ERROR_CODE);
		return {
			errorCode,
			title: getErrorTitle(errorCode),
			description: getUserFriendlyDescription(errorCode, error.message),
			rawMessage: error.message,
			stack: error.stack,
			details: {
				...commonDetails,
				errorType: error.name
			}
		};
	}

	const raw = safeStringify(error);
	const errorCode = String(options.defaultErrorCode ?? DEFAULT_ERROR_CODE);
	return {
		errorCode,
		title: getErrorTitle(errorCode),
		description: getUserFriendlyDescription(errorCode, raw),
		rawMessage: raw,
		raw,
		details: {
			...commonDetails,
			errorType: typeof error
		}
	};
}
